// New Referral Program (Invite & Earn)

import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Pagination } from '../models/pagination';
import { AffiliateRewardStep, AffiliateStats, AffiliateReward, AffiliateReferral } from '../models/affiliate';

@Injectable({
  providedIn: 'root'
})
export class AffiliateProgramService {

  private _rewardStepsQuery = `
    level
    amountFrom {amount currency}
    amountTo {amount currency}
    reward {amount currency}
  `;

  private _rewardsQuery = `
    totalCount
    edges {
      node {
        accountId
        dateDone 
        amount {amount currency}
      }
      cursor
    }
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
  `;

  private _referralsQuery = `
    totalCount
    edges {
      node {
        accountId
        status
        level
      }
      cursor
    }
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
  `;

  constructor(private api: ApiService) { }

  getAffiliateStats(first = 10, after: string = null): Observable<AffiliateStats> {
    return this.api.get(`query depositAndBuyReferralStats {
      depositAndBuyReferralStats {
        link
        referralId
        totalReward {
          amount
          currency
        }
        referrals(first: 1) {
          totalCount
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        const data = response['data']['depositAndBuyReferralStats'];

        if (data) {
          return of({
            link: data.link,
            totalReward: data.totalReward,
            rewardSteps: data.rewardSteps,
            referralsTotalCount: data.referrals ? data.referrals.totalCount : 0
          });
        } else {
          return throwError(response['data']);
        }
      }),
      this.api.unifyErrors()
    );
  }

  getAffiliateSteps(currency: string = null, auth = false): Observable<AffiliateRewardStep[]> {
    return this.api.get(`query depositAndBuyReferralRewards($currency: String) {
      depositAndBuyReferralRewards(currency: $currency) {
        steps {
          ${this._rewardStepsQuery}
        }
      }
    }`, {
      currency
    }, auth).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        const data = response['data'];

        if (data && data['depositAndBuyReferralRewards']) {
          return of(data['depositAndBuyReferralRewards']['steps']);
        } else {
          throwError(response); 
        }
      }),
      this.api.unifyErrors()
    );
  }

  getAffiliateRewards(first = 10, after: string): Observable<Pagination<AffiliateReward>> {
    return this.api.get(`query affiliateRewards($first: Int, $after: ID) {
      depositAndBuyReferralStats {
        rewards(first: $first, after: $after) {
          ${this._rewardsQuery}
        }
      }
    }`, {
        first,
        after
    }).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['depositAndBuyReferralStats']['rewards']);
      }),
      this.api.unifyErrors()
    );
  }

  getAffiliateReferrals(first = 10, after: string): Observable<Pagination<AffiliateReferral>> {
    return this.api.get(`query affiliateRefferals($first: Int, $after: ID) {
      depositAndBuyReferralStats {
        referrals(first: $first, after: $after) {
          ${this._referralsQuery}
        }
      }
    }`, {
        first,
        after
    }).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['depositAndBuyReferralStats']['referrals']);
      }),
      this.api.unifyErrors()
    );
  }
}
